
import { Button } from "@/components/ui/button";
import { Discrepancy } from "@/types/discrepancy";
import { useState } from "react";
import { RejectDiscrepancyDialog } from "./RejectDiscrepancyDialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";

interface DiscrepancyActionsProps {
  discrepancy: Discrepancy;
  onStatusUpdate: (id: string, status: string, explanation?: string, options?: {
    isSalaryDeduction?: boolean;
    salaryDeductionEmployee?: string;
    isForgiven?: boolean;
  }) => void;
  onJustifiedClick: (discrepancy: Discrepancy) => void;
}

export const DiscrepancyActions = ({
  discrepancy,
  onStatusUpdate,
  onJustifiedClick,
}: DiscrepancyActionsProps) => {
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectType, setRejectType] = useState<"salary" | "forgive">("salary");
  const location = useLocation();
  const isStaffPage = location.pathname.includes("/staff");

  const handleRejectClick = (type: "salary" | "forgive") => {
    setRejectType(type);
    setIsRejectDialogOpen(true);
  };

  const handleSalaryDeduction = async (options: {
    isSalaryDeduction: boolean;
    salaryDeductionEmployee?: string;
    isForgiven: boolean;
  }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Utilisateur non authentifié");
        return;
      }

      // Validate the counted amount in validated_amounts
      const { error: validationError } = await supabase
        .from("validated_amounts")
        .insert({
          counting_date: discrepancy.counting_date,
          payment_method: discrepancy.payment_method,
          amount: discrepancy.counted_amount,
          status: "validated",
          owner_id: user.id
        });

      if (validationError) {
        console.error("Error validating amount:", validationError);
        toast.error("Erreur lors de la validation du montant");
        return;
      }

      // Update poultry transactions to mark them as validated revenue
      const { error: transactionError } = await supabase
        .from("poultry_transactions")
        .update({ is_deducted_from_revenue: true })
        .eq("created_by", user.id)
        .eq("created_at", discrepancy.counting_date);

      if (transactionError) {
        console.error("Error updating transactions:", transactionError);
        toast.error("Erreur lors de la mise à jour des transactions");
        return;
      }

      // Finally update the discrepancy status
      const status = options.isSalaryDeduction ? "salary_deduction" : "forgiven";
      onStatusUpdate(discrepancy.id, status, undefined, options);
      setIsRejectDialogOpen(false);
      toast.success("Montant validé avec succès");
    } catch (error) {
      console.error("Error in handleSalaryDeduction:", error);
      toast.error("Une erreur est survenue");
    }
  };

  // N'affiche les boutons que sur la page du personnel
  if (!isStaffPage) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {discrepancy.status === "pending" && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onJustifiedClick(discrepancy)}
            className="flex-shrink-0"
          >
            Justifier
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRejectClick("salary")}
            className="text-orange-600 hover:text-orange-700 flex-shrink-0"
          >
            Retenue sur salaire
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRejectClick("forgive")}
            className="text-blue-600 hover:text-blue-700 flex-shrink-0"
          >
            Pardonner
          </Button>

          <RejectDiscrepancyDialog
            open={isRejectDialogOpen}
            onOpenChange={setIsRejectDialogOpen}
            discrepancy={discrepancy}
            rejectType={rejectType}
            onSubmit={handleSalaryDeduction}
          />
        </>
      )}
    </div>
  );
};
